import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Heading,
  Paragraph,
  Skeleton,
  Tag,
} from '@digdir/designsystemet-react';
import type { HrSafetyReps as SafetyRepsData } from '../../../api/hrSafetyReps';
import {
  useHrSafetyRepsQuery,
  useRemoveSafetyRepMutation,
} from '../../../hooks/queries/hr/useHrSafetyRepsQuery';
import { SafetyRepDialog } from './SafetyRepDialog';
import { teamNames, teamsWithoutSafetyRep } from './safetyRepsView';
import classes from './HRSafetyReps.module.css';

type Representative = SafetyRepsData['representatives'][number];

const RepresentativeRow = ({
  rep,
  data,
  onEdit,
}: {
  rep: Representative;
  data: SafetyRepsData;
  onEdit: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const remove = useRemoveSafetyRepMutation();
  return (
    <li>
      <span className={classes.person}>
        <span className="ds-label" data-size="sm">
          {rep.email}
          {rep.isMain && (
            <>
              {' '}
              <Tag data-size="sm" data-color="info">
                {t('hrSafetyReps.main')}
              </Tag>
            </>
          )}
        </span>
        <Paragraph data-size="sm" className={classes.subtle}>
          {rep.isMain
            ? t('hrSafetyReps.allTeams')
            : teamNames(rep, data.teams).join(', ')}
        </Paragraph>
      </span>
      {rep.isMain ? (
        <Paragraph data-size="xs" className={classes.subtle}>
          {t('hrSafetyReps.mainByAdmin')}
        </Paragraph>
      ) : confirming ? (
        <span className={classes.actions}>
          <Paragraph data-size="sm">
            {t('hrSafetyReps.confirmRemove', { email: rep.email })}
          </Paragraph>
          <Button
            variant="secondary"
            data-color="danger"
            data-size="sm"
            loading={remove.isPending}
            onClick={() => remove.mutate(rep.userId)}
          >
            {t('hrSafetyReps.remove')}
          </Button>
          <Button
            variant="tertiary"
            data-size="sm"
            onClick={() => setConfirming(false)}
          >
            {t('hrSafetyReps.cancel')}
          </Button>
        </span>
      ) : (
        <span className={classes.actions}>
          <Button variant="secondary" data-size="sm" onClick={onEdit}>
            {t('hrSafetyReps.editTeams')}
          </Button>
          <Button
            variant="tertiary"
            data-size="sm"
            onClick={() => setConfirming(true)}
          >
            {t('hrSafetyReps.remove')}
          </Button>
        </span>
      )}
      {remove.isError && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t('hrSafetyReps.failed')}</Paragraph>
        </Alert>
      )}
    </li>
  );
};

/** The safety representatives the employees have elected, and the teams each one represents */
export const HRSafetyReps = (): ReactElement => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useHrSafetyRepsQuery();
  const [dialog, setDialog] = useState<{
    editing: Representative | null;
  } | null>(null);
  const missing = data ? teamsWithoutSafetyRep(data) : [];

  return (
    <div className={classes.page} data-size="md">
      <div className={classes.hero}>
        <Heading level={1} data-size="xl">
          {t('hrSafetyReps.title')}
        </Heading>
        <Paragraph className={classes.subtle}>
          {t('hrSafetyReps.intro')}
        </Paragraph>
      </div>
      {isLoading && <Skeleton variant="rectangle" width="100%" height={240} />}
      {isError && (
        <Alert data-color="danger">
          <Paragraph>{t('hrSafetyReps.error')}</Paragraph>
        </Alert>
      )}
      {data && (
        <>
          {missing.length > 0 && (
            <Alert data-color="warning" data-size="sm">
              <Paragraph data-size="sm">
                {t('hrSafetyReps.missing', {
                  count: missing.length,
                  teams: missing.map((team) => team.name).join(', '),
                })}
              </Paragraph>
            </Alert>
          )}
          <section
            aria-labelledby="safety-reps-heading"
            className={classes.card}
          >
            <div className={classes.heading}>
              <Heading level={2} data-size="sm" id="safety-reps-heading">
                {t('hrSafetyReps.listTitle')}
              </Heading>
              <Button
                data-size="sm"
                onClick={() => setDialog({ editing: null })}
              >
                {t('hrSafetyReps.registerTitle')}
              </Button>
            </div>
            {data.representatives.length === 0 ? (
              <Paragraph data-size="sm">{t('hrSafetyReps.none')}</Paragraph>
            ) : (
              <ul className={classes.list}>
                {data.representatives.map((rep) => (
                  <RepresentativeRow
                    key={rep.userId}
                    rep={rep}
                    data={data}
                    onEdit={() => setDialog({ editing: rep })}
                  />
                ))}
              </ul>
            )}
          </section>
          {dialog && (
            <SafetyRepDialog
              data={data}
              editing={dialog.editing}
              onClose={() => setDialog(null)}
            />
          )}
        </>
      )}
    </div>
  );
};
